<template>
  <div class="c-results">
    <search-filter
      :searchTerm.sync="searchTerm"
      :searchLabel="searchLabel"
      v-on:formSearch="submitSearch"
    >
    </search-filter>

    <div v-if="!initialLoad">
      <search-nav
        :sites="sitesNav"
        :activeIndex="activeIndex"
        :searchDescription="searchDescription"
        v-on:getResults="setSiteContent"
      ></search-nav>
      <div class="c-results__container">
        <search-page
          v-for="(page, index) in pageContent"
          :key="page.id"
          :isActive="activeIndex === index"
          :searchTerm="searchTerm"
          :totalResults="totalResults(page)"
          :pageResults="pageResults(page)"
          :documentResults="documentResults(page)"
        >
          <search-tablist>
            <search-tab
              name="searchResultsPages"
              panelSuffix="Panel"
              tabSuffix="Tab"
              :isActive="isActiveTab(page)"
              title="Pages"
              :resultsNumber="pageResults(page)"
            >
              <search-listing
                :siteUrl="sites[index].siteUrl"
                item-component="SearchItemPage"
                :resultsListing="page.pages.results"
                v-if="page.pages.results"
                v-on:searchResultClick="handleSearchClick"
              >
              </search-listing>
              <search-paging
                searchType="pages"
                :currentPage="page.pages.pageNumber"
                :totalPages="page.pages.totalPages"
                v-on:getResults="(page, searchType) =>
                    getSearchResults(page, searchType, sites[index])"
                v-if="hasResults(page.pages.results)"
              >
              </search-paging>
              <search-error v-if="page.pages.errored"></search-error>
              <search-no-results
                searchType="pages"
                :searchTerm="searchTerm"
                v-if="!page.pages.errored && !hasResults(page.pages.results)"
              >
              </search-no-results>
            </search-tab>
            <search-tab
              name="searchResultsDocuments"
              panelSuffix="Panel"
              tabSuffix="Tab"
              :isActive="!isActiveTab(page)"
              title="Documents"
              :resultsNumber="documentResults(page)"
              v-if="page.documents"
            >
              <search-listing
                :siteUrl="sites[index].siteUrl"
                item-component="SearchItemDoc"
                :resultsListing="page.documents.results"
                v-if="page.documents.results"
                v-on:searchResultClick="handleSearchClick"
              >
              </search-listing>
              <search-paging
                searchType="documents"
                :currentPage="page.documents.pageNumber"
                :totalPages="page.documents.totalPages"
                v-on:getResults="
                  (page, searchType) =>
                    getSearchResults(page, searchType, sites[index])
                "
                v-if="hasResults(page.documents.results)"
              >
              </search-paging>
              <search-error v-if="page.documents.errored"></search-error>
              <search-no-results
                searchType="documents"
                :searchTerm="searchTerm"
                v-if="!page.documents.errored && !hasResults(page.documents.results)"
              >
              </search-no-results>
            </search-tab>
          </search-tablist>
        </search-page>
      </div>
    </div>
    <search-loading v-if="loading"></search-loading>
  </div>
</template>

<script>
  import "url-search-params-polyfill";
  import axios from "axios";
  import endpoints from "Globals/mando.endpoints";
  import SearchError from "./SearchError.vue";
  import SearchFilter from "./SearchFilter.vue";
  import SearchListing from "./SearchListing.vue";
  import SearchLoading from "./SearchLoading.vue";
  import SearchNav from "./SearchNav.vue";
  import SearchNoResults from "./SearchNoResults.vue";
  import SearchPage from "./SearchPage.vue";
  import SearchPaging from "./SearchPaging.vue";
  import SearchTab from "./SearchTab.vue";
  import SearchTablist from "./SearchTablist.vue";

  const ENDPOINTS = {
    documents: "getSearchResultsDoc",
    pages: "getSearchResultsPage",
  };

  export default {
    name: "SearchResults",
    components: {
      SearchError,
      SearchFilter,
      SearchListing,
      SearchLoading,
      SearchNav,
      SearchNoResults,
      SearchPage,
      SearchPaging,
      SearchTab,
      SearchTablist,
    },
    props: {
      searchLabel: String,
      searchDescription: String,
      sites: {
        required: true,
        type: Array,
      },
    },
    data() {
      return {
        activeIndex: 0,
        delay: 500,
        docResultsError: false,
        initialLoad: true,
        loading: true,
        pageContent: this.resetPageContent(),
        sitesNav: this.initializeSitesNav(),
        siteId: this.sites[0].indexName,
        pageResultsError: false,
        searchTerm: "",
        timer: null,
      };
    },
    mounted() {
      this.parseQueryString();
    },
    computed: {
      totalResults() {
        return (page) => this.calculateTotalResults(page);
      },
      pageResults() {
        return (page) => page.pages.totalResults || 0;
      },
      documentResults() {
        return (page) => page.documents.totalResults || 0;
      },
      hasResults() {
        return (results) => results && results.length > 0;
      },
    },
    methods: {
      async setSiteContent(index) {
        this.activeIndex = index;
      },

      async getSearchResults(page, searchType, site) {
        const indexParam =
          searchType === "pages" ? site.indexName : site.mediaIndexName;
        await axios
          .get(
            `${endpoints(ENDPOINTS[searchType])}
						?searchTerm=${this.searchTerm}
						&page=${page}
						&siteId=${indexParam}`
          )
          .then((res) => {
            this.getSearchResultsSuccess(res, searchType, site);
          })
          .catch((error) => {
            this.setErrors(searchType, site);
          });
      },

      async getSearchResultsSuccess(response, searchType, site) {
        const {
          results,
          pageNumber,
          totalPages,
          totalResults,
          queryId,
        } = response.data;
        const dataIndex = this.pageContent.findIndex(
          (item) => item.id === site.indexName
        );
        const data = {
          results,
          pageNumber,
          totalPages,
          totalResults,
          queryId,
          errored: false
        };
        if (dataIndex > -1) {
          this.pageContent[dataIndex][searchType] = data;
        } else {
          this.pageContent.push({ id: site.indexName, [searchType]: data });
        }
      },

      setErrors(searchType = null, site = null) {
        if(!searchType && !site){
          for (let i = 0; i < this.pageContent.length; i++) {
            this.pageContent[i].documents = { errored: true };
            this.pageContent[i].pages = { errored: true };
          }
          return;
        }

        const dataIndex = this.pageContent.findIndex(
          (item) => item.id === site.indexName
        );

        const data = {errored: true};

        if (dataIndex > -1) {
          if (searchType) {
            this.pageContent[dataIndex][searchType] = data;
          } 
          else {
            this.pageContent[dataIndex].documents = data;
            this.pageContent[dataIndex].pages = data;
          }
        } 
        else {
          if (searchType) {
            this.pageContent.push({ id: site.indexName, [searchType]: data });
          } 
          else {
            this.pageContent.push({ id: site.indexName, documents: data, pages: data });
          }
        }
      },

      startLoadingEquence() {
        this.loading = true;
      },

      endLoadingEquence() {
        this.timer = setTimeout(() => {
          this.initialLoad = false;
          this.loading = false;
          this.timer = null;
        }, this.delay);
      },

      async parseQueryString() {
        const queryString = new URLSearchParams(window.location.search);
        if (queryString.has("searchTerm")) {
          await this.submitSearch(queryString.get("searchTerm"));
        } else {
          this.loading = false;
        }
      },

      resetPageContent() {
        this.pageContent = this.sites.map((site) => ({
          id: site.indexName,
          pages: {},
          documents: {},
        }));
      },

      async submitSearch(searchTerm) {
        this.searchTerm = searchTerm;
        this.startLoadingEquence();
        this.resetPageContent();
        try {
          await Promise.all(
            this.sites.map((site, index) => {
              return Promise.all([
                this.getSearchResults(1, "documents", site, index),
                this.getSearchResults(1, "pages", site, index),
              ]);
            })
          );
          this.updateSitesNav();
          this.endLoadingEquence();
        } catch (error) {
          this.setErrors();
        }
      },

      calculateTotalResults(page) {
        return (
          (page.pages.totalResults || 0) + (page.documents.totalResults || 0)
        );
      },

      isActiveTab(page) {
        const { pages, documents } = page;
        const results = pages.results && documents.results;
        if (results) {
          const noPages = pages.results.length < 1;
          const hasDocs = documents.results.length > 0;
          return noPages && hasDocs ? false : true;
        }
        return false;
      },

      initializeSitesNav() {
        return this.sites.map((site) => ({
          id: site.indexName,
          siteLabel: site.siteLabel,
          totalResults: 0,
        }));
      },

      updateSitesNav() {
        this.pageContent.map((page) => {
          const pageSiteNav = this.sitesNav.filter(
            (siteNav) => siteNav.id === page.id
          )[0];
          if (pageSiteNav) {
            pageSiteNav.totalResults = this.totalResults(page);
          }
        });
      },

      async handleSearchClick(searchType, objectId, position) {
        const site = this.sites[this.activeIndex];
        const siteIndex =
          searchType === "pages" ? site.indexName : site.mediaIndexName;
        const queryId = this.pageContent[this.activeIndex][searchType].queryId;

        const response = await axios.post(
          `${endpoints(
            "postSearchEvent"
          )}?index=${siteIndex}&queryId=${queryId}&objectId=${objectId}&position=${position}`
        );
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/components/consumer/results/SearchResults.vue